/**
 * ADR 023 (BL-1285) — `deriveAuctionPhaseKind`: derive the canonical
 * {@link AuctionPhaseKind} from an auction row's decision stamps, WITHOUT
 * loading the auction's lots. This is the derivation every admin surface reads.
 *
 * BL-1516 retired the `post-sale` rung, which was the ONLY rung that needed
 * anything beyond the auction's own stamps (the two BL-639 denorm lot
 * aggregates + a clock). The ladder is therefore pure stamp precedence now, and
 * `deriveAuctionPhaseCoarse` — which was defined as "this ladder minus
 * `post-sale`" — is the SAME function. `derive-auction-phase-coarse.ts` re-exports
 * this one rather than keeping a second copy of the ladder to drift against;
 * collapsing the two NAMES is declared BL-1516 consolidation residue.
 *
 * "Has this sale finished?" — the question the `post-sale` rung actually
 * answered for its non-cosmetic callers — is now {@link isAuctionAwaitingClose}
 * (and its SQL twin `auctionAwaitingCloseWhere`), which is deliberately a
 * boolean over the `active` phase and not a lifecycle value.
 *
 * Pure IsomorphicService (ADR 010 / sharing.md §3) — no I/O, no auth, no
 * `@auctogon/db` import; client-safe.
 *
 * The legacy 6-value `auction_status` label used to be a PROJECTION of this
 * ladder — implemented by calling this with `pausedAt: null` and mapping the
 * resulting kind to its word, which reproduced it rung-for-rung because that
 * label deliberately never had a `Paused` value. BL-1444 deleted it with its
 * only consumer (`apps/admin-catalog/`), so this ladder is now the only auction
 * phase derivation of this shape, and BL-1584 took the label's name off the row
 * type and column helpers it had lent them.
 *
 * Pure stamp/denorm precedence, type-agnostic — same posture as
 * `deriveAuctionPhaseCoarse`. (An earlier draft justified this helper partly on
 * `deriveAuctionPhase` throwing for the
 * `Live Webcast` type; BL-1128 retired that type at the database layer, so the
 * remaining — and sufficient — justification is the lots-array one above.)
 */
import type { AuctionPhaseKind } from './phase-tokens';

/**
 * The auction fields this helper reads — the six decision stamps, and nothing
 * else. Timestamps are ISO strings (Drizzle `mode: 'string'`).
 *
 * BL-1516 removed `lotsOpenCount` / `lotsMaxCloseTime` / `now`: they existed
 * solely for the retired `post-sale` rung. Callers that spread a wider row
 * (`{ ...row, now }`) are unaffected; the extra fields are simply ignored.
 */
export interface AuctionPhaseKindInput {
  deletedAt: string | null;
  closedAt: string | null;
  readiedAt: string | null;
  openedAt: string | null;
  /** `auctions.paused_at` — drives the `paused` rung. */
  pausedAt: string | null;
  /** `auctions.pre_bidding_at` (BL-1291) — read only while `openedAt` is null. */
  preBiddingAt: string | null;
}

/**
 * Derive an auction's canonical ADR-023 phase kind.
 *
 * Precedence (first match wins) — the canonical ladder from phase-tokens.ts,
 * with the lots-array inputs substituted by their denorm aggregates:
 *   deletedAt          → 'deleted'
 *   closedAt           → 'closed'
 *   readiedAt null     → 'draft'
 *   openedAt null AND preBiddingAt set → 'accepting-pre-bids'
 *   openedAt  null     → 'ready'
 *   pausedAt           → 'paused'
 *   otherwise          → 'active'
 *
 * @param input The decision stamps + denorm aggregates + clock (see {@link AuctionPhaseKindInput}).
 * @returns The {@link AuctionPhaseKind}.
 * @sideEffect none — pure.
 */
export function deriveAuctionPhaseKind(input: AuctionPhaseKindInput): AuctionPhaseKind {
  if (input.deletedAt != null) return 'deleted';
  if (input.closedAt != null) return 'closed';
  if (input.readiedAt == null) return 'draft';
  // BL-1291: the auction's own pre-bidding stamp, read only while it has not
  // opened — so opening for bidding ends pre-bidding with no separate write.
  if (input.openedAt == null && input.preBiddingAt != null) return 'accepting-pre-bids';
  if (input.openedAt == null) return 'ready';
  if (input.pausedAt != null) return 'paused';
  return 'active';
}

/**
 * The auction-level inputs {@link isAuctionAwaitingClose} reads: the derived
 * phase plus the two BL-639 denorm lot aggregates and a clock.
 */
export interface AuctionAwaitingCloseInput {
  /** The auction's derived phase (from {@link deriveAuctionPhaseKind}). */
  phase: AuctionPhaseKind;
  /** `auctions.lots_open_count_denormalized`: count of not-yet-terminal,
   *  non-deleted lots. `0` ⇔ all lots terminal (vacuously true for a lot-less
   *  auction). */
  lotsOpenCount: number;
  /** `auctions.lots_max_close_time_denormalized`: MAX effective lot close, or
   *  `null` when there is no lot close time (treated as past-close). */
  lotsMaxCloseTime: string | null;
  /** Clock for the past-close comparison (server: `new Date()`). */
  now: Date;
}

/**
 * BL-1516 — "this auction's sale has finished, but nobody has stamped
 * `closed_at` yet": the auction derives `active`, every non-deleted lot has
 * reached a terminal disposition, and the last lot's close time has passed.
 *
 * This is EXACTLY the condition the retired `post-sale` phase rung tested. It is
 * deliberately NOT a lifecycle value: no operator can act on it, no transition
 * targets it, and it resolves itself within a minute (the `close-due-timed-lots`
 * cron's second phase selects this same set and closes it). It is a boolean
 * about bookkeeping, and the surfaces that need it ask for it by name:
 *
 * - the close sweep, to pick the auctions to stamp;
 * - the bidder's public status + watchlist + lot buckets, so a finished sale
 *   reads "ended" rather than "open for bidding" during that window (BUG-1170);
 * - bidder registration, which must not open on a finished sale;
 * - the reopen landing gate, which must re-run the go-live blockers when a lot
 *   is reopened under a finished auction (BUG-1506, matrix §7p) — folding that
 *   into plain `active` would refuse ordinary mid-sale lot reopening instead.
 *
 * Pure IsomorphicService; the SQL twin is `auctionAwaitingCloseWhere` and the
 * Postgres parity test holds the two in step.
 *
 * @param input - the derived phase + denorm aggregates + clock.
 * @returns true when the sale is over and `closed_at` has not been stamped.
 * @sideEffect none — pure.
 */
export function isAuctionAwaitingClose(input: AuctionAwaitingCloseInput): boolean {
  if (input.phase !== 'active') return false;
  if (input.lotsOpenCount !== 0) return false;
  return (
    input.lotsMaxCloseTime == null || input.now.getTime() >= Date.parse(input.lotsMaxCloseTime)
  );
}

/** The phases {@link isPreOpenAuctionPhase} accepts — readied, not yet opened. */
export type PreOpenAuctionPhaseKind = Extract<AuctionPhaseKind, 'ready' | 'accepting-pre-bids'>;

/**
 * BL-1291 — "this auction has not opened for bidding yet": `ready` OR
 * `accepting-pre-bids`.
 *
 * The `accepting-pre-bids` rung SHADOWS `ready`. Before it existed, an auction
 * with pre-bidding open derived `ready`, so every gate written as
 * `phase === 'ready'` matched it. The moment the rung landed, all of those
 * silently stopped matching — and they fail QUIETLY: the auction-open cron
 * simply selects nothing, close-time maintenance simply doesn't run, reminders
 * simply aren't sent. No error, no failing test.
 *
 * Use this predicate instead of `=== 'ready'` wherever the question is really
 * "is bidding still un-opened?" — which is almost everywhere `ready` is tested
 * outside the transition catalog, where `ready` means the specific rung.
 * Its SQL twin for query-side gates is `preOpenAuctionPhaseWhere()`.
 *
 * Narrows, so a caller that gates on it can keep a tight union downstream
 * instead of re-widening to the whole ladder.
 *
 * @param kind - the auction's derived phase.
 * @returns true when the auction is readied but bidding has not opened.
 */
export function isPreOpenAuctionPhase(kind: AuctionPhaseKind): kind is PreOpenAuctionPhaseKind {
  return kind === 'ready' || kind === 'accepting-pre-bids';
}

/** The phases {@link isOpenedAuctionPhase} accepts — bidding has been opened. */
export type OpenedAuctionPhaseKind = Extract<
  AuctionPhaseKind,
  'active' | 'paused' | 'closed'
>;

/**
 * BL-1289 — "this auction HAS opened for bidding": `active`, `paused` or
 * `closed`. The mirror of {@link isPreOpenAuctionPhase}, and the complement of
 * it plus `draft` / `deleted`.
 *
 * `paused` is included because a paused auction IS an opened one — it has
 * `opened_at` set and merely has its clocks halted. `closed` is included because
 * the auction ran: the stamp is set and moving off it is a walk-back, not a
 * first open. (BL-1516: `post-sale` was a fourth member; it folded into
 * `active`, which was already here.)
 *
 * This is the predicate that answers **"would moving to X un-open this
 * auction?"** — the question both sides of the wire now ask:
 *
 * - **Server:** `statusDecisionStampSet` clears `opened_at` / `paused_at` for any
 *   target this returns false for. That clear is the un-open.
 * - **Client:** the settings status control confirms any move OUT of a phase this
 *   returns true for, because that move pulls a running sale off the bidder site.
 *
 * Extracted here (from a private copy in `auction-lifecycle-stamp-repository.ts`)
 * precisely so those two can't answer it differently — a client that disagreed
 * with the stamp mapper about which phases are "opened" would skip the
 * confirmation on exactly the moves that need it (`sharing.md` § 3).
 *
 * Narrows, so a caller gating on it keeps a tight union downstream.
 *
 * @param kind - the auction's derived phase.
 * @returns true when bidding has been opened (whether or not it is still running).
 */
export function isOpenedAuctionPhase(kind: AuctionPhaseKind): kind is OpenedAuctionPhaseKind {
  return kind === 'active' || kind === 'paused' || kind === 'closed';
}

/** The phases {@link isPublishedScheduleAuctionPhase} accepts — schedule is live on the website. */
export type PublishedScheduleAuctionPhaseKind = Extract<
  AuctionPhaseKind,
  'ready' | 'accepting-pre-bids' | 'active' | 'paused'
>;

/**
 * BL-1575 — "is this auction's schedule advertised to buyers right now?":
 * `ready`, `accepting-pre-bids`, `active` or `paused`.
 *
 * This is the arming gate for `auctions.published_dates_changed_at`. Moving
 * `start_date` or `first_lot_close_at` on an auction in one of these phases owes
 * the audience a notice; moving it anywhere else does not — a draft was never
 * published, and a finished or deleted sale has nothing left to act on.
 *
 * **Do NOT rewrite this as `readied_at IS NOT NULL`.** They are not the same set,
 * and the difference is silent. `draft` sits at rung 3 of the ladder in
 * {@link deriveAuctionPhaseKind}, BELOW `deleted` and `closed` — so the column
 * test is the complement of `draft` ALONE and additionally admits `closed` and
 * `deleted`, both of which still carry the stamp. A gate written against the
 * column arms a notice on an auction that has already run. (BL-1575's own card
 * asserted the two coincide; they do not. BL-1516 removed a third member,
 * `post-sale`, from that list of extras.)
 *
 * Derive the phase from a row read under the SAME lock as the write, not from an
 * earlier unlocked snapshot: an auction that closes in between would otherwise
 * still arm.
 *
 * A strict superset of {@link isPreOpenAuctionPhase}, which was the gate the
 * now-deleted automatic fan-out used (BL-1291) — the operator-triggered notice
 * deliberately reaches running sales too.
 *
 * Narrows, so a caller gating on it keeps a tight union downstream.
 *
 * @param kind - the auction's derived phase.
 * @returns true when the auction's dates are publicly advertised.
 */
export function isPublishedScheduleAuctionPhase(
  kind: AuctionPhaseKind,
): kind is PublishedScheduleAuctionPhaseKind {
  return kind === 'ready' || kind === 'accepting-pre-bids' || kind === 'active' || kind === 'paused';
}
